import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Star, ChevronLeft, ChevronRight, Palette, RefreshCw, Search, Share2, Target, Zap, Award, Users, TrendingUp, CheckCircle2, Sparkles, Layers, Code2, Megaphone } from 'lucide-react';
import axios from 'axios';
import { CodeRain } from '../components/effects/CodeRain';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const coreServices = [
  { name: 'Web Design', icon: Palette, description: 'Stunning websites that convert' },
  { name: 'Branding', icon: Sparkles, description: 'Memorable brand identities' },
  { name: 'SEO', icon: Search, description: 'Rank higher, grow faster' },
  { name: 'Social Media', icon: Share2, description: 'Engage your audience' },
  { name: 'AI Automation', icon: Zap, description: 'Smart receptionist & workflows' },
];

const portfolioItems = [
  { 
    title: 'Caveman Financial',
    category: 'Finance & Research',
    description: 'Small cap investment research platform with real-time stock tracking, sector analysis, and market insights',
    result: 'Live Stock Data + Newsletter',
    image: '/CF Screen.png',
    link: 'https://caveman-financial.com/'
  },
  {
    title: 'Plan Testosterone',
    category: 'Health & Wellness',
    description: 'Awareness platform about testosterone decline with research-backed insights and community building',
    result: 'Health Education Hub',
    image: '/PT Screen.png',
    link: 'https://plan-testosterone.com/'
  },
  { 
    title: 'UniGem',
    category: 'Mobile App',
    description: 'iOS application available on the App Store - innovative mobile solution for users',
    result: 'Live on App Store',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    link: 'https://apps.apple.com/us/app/unigem/id6740437581'
  },
];

const clientBenefits = [
  { icon: Layers, title: 'Custom Strategy', description: 'Tailored solutions based on your unique business goals and market position.' },
  { icon: Code2, title: 'Modern Technology', description: 'Built with the latest tech stack for speed, security, and scalability.' },
  { icon: TrendingUp, title: 'Data-Driven Results', description: 'Every decision backed by analytics and measurable outcomes.' },
  { icon: Megaphone, title: 'Brand Amplification', description: 'Strategies that increase visibility and establish market authority.' },
  { icon: Users, title: 'Dedicated Support', description: 'A committed team working as an extension of your business.' },
  { icon: Award, title: 'Proven Excellence', description: 'Award-winning work recognized across the industry.' },
];

const stats = [
  { value: '180%', label: 'Avg. Conversion Increase', suffix: '+' },
  { value: '50', label: 'Projects Conceptualized', suffix: '+' },
  { value: '10', label: 'Tech Stack Options', suffix: '+' },
  { value: '95', label: 'Client Retention', suffix: '%' },
];

const processSteps = [
  { num: '01', title: 'Discovery', description: 'We dive deep into your business, goals, and target audience.' },
  { num: '02', title: 'Strategy', description: 'Craft a tailored roadmap for design and marketing success.' },
  { num: '03', title: 'Design', description: 'Create stunning visuals that capture your brand essence.' },
  { num: '04', title: 'Launch', description: 'Deploy and optimize for maximum impact and conversions.' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentPortfolio, setCurrentPortfolio] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const testimonialsRes = await axios.get(`${API}/testimonials`);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const nextTestimonial = () => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  const nextPortfolio = () => setCurrentPortfolio((prev) => (prev + 1) % portfolioItems.length);
  const prevPortfolio = () => setCurrentPortfolio((prev) => (prev - 1 + portfolioItems.length) % portfolioItems.length);

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#050505]">
      {/* ===== HERO SECTION ===== */}
      <section className="min-h-screen relative flex items-center overflow-hidden">
        {/* Background Layers */}
        <CodeRain />
        <div className="absolute inset-0 z-[1]" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')`,
          backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.15
        }} />
        <div className="absolute inset-0 z-[2] opacity-30" style={{
          backgroundImage: `linear-gradient(rgba(0, 87, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 87, 255, 0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/60 to-[#050505] z-[3]" />
        
        {/* Glowing Orbs */}
        <div className="absolute top-20 right-1/4 w-[500px] h-[500px] bg-blue-600/25 blur-[120px] rounded-full z-[2]" />
        <div className="absolute bottom-20 left-1/4 w-[400px] h-[400px] bg-cyan-500/15 blur-[100px] rounded-full z-[2]" />

        {/* Hero Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32 sm:py-40 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div initial="hidden" animate="visible" variants={containerVariants}>
              <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/20 border border-blue-500/30 mb-8">
                <Zap className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-400">Next Gen Development</span>
              </motion.div>
              
              <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] text-white font-['Clash_Display'] mb-6">
                Elevate Your Business:
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500">Design & Marketing</span>
                <br />
                That Converts
              </motion.h1>
              
              <motion.p variants={itemVariants} className="text-lg sm:text-xl leading-relaxed text-slate-300 max-w-xl mb-10">
                We craft stunning digital experiences and powerful marketing strategies that transform your business into a market leader.
              </motion.p>
              
              <motion.div variants={itemVariants} className="flex flex-wrap gap-4">
                <Link to="/quote" data-testid="hero-get-quote-btn" className="group inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-lg px-8 py-4 rounded-full transition-all duration-300 shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40">
                  Get a Free Quote
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/services" data-testid="hero-view-services-btn" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white font-semibold text-lg px-8 py-4 rounded-full border border-white/20 hover:border-white/40 transition-all duration-300">
                  Our Skills
                </Link>
              </motion.div>
            </motion.div>

            {/* Right - Featured Work Preview */}
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="relative hidden lg:block">
              <div className="relative">
                {/* Monitor Frame */}
                <div className="relative bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] rounded-2xl p-3 border border-white/10">
                  <div className="flex gap-1.5 mb-3">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <div className="rounded-lg overflow-hidden aspect-[16/10]">
                    <AnimatePresence mode="wait">
                      <motion.img 
                        key={currentPortfolio}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        src={portfolioItems[currentPortfolio].image}
                        alt={portfolioItems[currentPortfolio].title}
                        className="w-full h-full object-cover"
                      />
                    </AnimatePresence>
                  </div>
                </div>
                
                {/* Monitor Stand */}
                <div className="w-24 h-8 mx-auto bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] rounded-b-lg" />
                <div className="w-40 h-2 mx-auto bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] rounded-full" />

                {/* Portfolio Navigation */}
                <div className="flex justify-center gap-3 mt-6">
                  <button onClick={prevPortfolio} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all">
                    <ChevronLeft size={18} />
                  </button>
                  <div className="flex items-center gap-2">
                    {portfolioItems.map((_, i) => (
                      <button key={i} onClick={() => setCurrentPortfolio(i)} className={`h-2 rounded-full transition-all ${i === currentPortfolio ? 'w-8 bg-blue-500' : 'w-2 bg-white/20'}`} />
                    ))}
                  </div>
                  <button onClick={nextPortfolio} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all">
                    <ChevronRight size={18} />
                  </button>
                </div>

                {/* Floating Project Info Card */}
                <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="absolute -bottom-4 -left-8 z-20">
                  <div className="bg-[#0d0d0d]/95 backdrop-blur-xl border border-white/10 rounded-xl p-4 shadow-2xl max-w-[200px]">
                    <span className="text-blue-400 text-xs font-bold uppercase tracking-wider">{portfolioItems[currentPortfolio].category}</span>
                    <h4 className="text-white font-semibold mt-1 text-sm">{portfolioItems[currentPortfolio].title}</h4>
                    <p className="text-green-400 text-xs mt-1">{portfolioItems[currentPortfolio].result}</p>
                  </div>
                </motion.div>

                {/* Floating Stats Card */}
                <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} className="absolute -top-4 -right-4 z-20">
                  <div className="bg-[#0d0d0d]/95 backdrop-blur-xl border border-white/10 rounded-xl p-4 shadow-2xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Star className="w-5 h-5 text-blue-400" fill="currentColor" />
                      </div>
                      <div>
                        <p className="text-white font-bold">Top Rated</p>
                        <p className="text-slate-400 text-xs">50+ Reviews</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
            <motion.div animate={{ y: [0, 12, 0] }} transition={{ duration: 2, repeat: Infinity }} className="w-1.5 h-1.5 rounded-full bg-blue-500" />
          </div>
        </motion.div>
      </section>

      {/* ===== STATS SECTION ===== */}
      <section className="py-16 bg-gradient-to-r from-blue-600/10 via-[#050505] to-blue-600/10 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={stat.label} variants={itemVariants} className="text-center">
                <p className="text-4xl sm:text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-['Clash_Display']">
                  {stat.suffix === '+' && '+'}{stat.value}{stat.suffix === '%' && '%'}
                </p>
                <p className="text-slate-400 mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section className="py-24 sm:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants} className="text-center mb-16">
            <motion.span variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-500/20 mb-6">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-400">What We Do</span>
            </motion.span>
            <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Clash_Display']">
              Services That Drive <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Results</span>
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {coreServices.map((service) => (
              <motion.div key={service.name} variants={itemVariants} whileHover={{ scale: 1.05, y: -8 }} className="group p-6 rounded-2xl bg-gradient-to-b from-white/5 to-transparent border border-white/10 hover:border-blue-500/50 transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 mb-4 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center group-hover:bg-blue-600/30 group-hover:scale-110 transition-all duration-300">
                  <service.icon className="text-blue-400" size={26} />
                </div>
                <h3 className="text-white font-semibold mb-2 font-['Clash_Display'] text-lg">{service.name}</h3>
                <p className="text-slate-400 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={itemVariants} className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services-btn" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-semibold transition-colors">
              Explore All Services <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== FEATURED WORK SECTION ===== */}
      <section className="py-24 sm:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants}>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div>
                <motion.span variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-500/20 mb-6">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-400">Featured Work</span>
                </motion.span>
                <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Clash_Display']">
                  Browse Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Portfolio</span>
                </motion.h2>
              </div>
              <motion.div variants={itemVariants}>
                <Link to="/services" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white font-medium px-6 py-3 rounded-full border border-white/10 hover:border-white/20 transition-all">
                  View All Projects <ArrowRight size={16} />
                </Link>
              </motion.div>
            </div>

            <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {portfolioItems.map((project, index) => (
                <motion.a 
                  key={project.title} 
                  href={project.link}
                  target={project.link.startsWith('http') ? '_blank' : '_self'}
                  rel={project.link.startsWith('http') ? 'noopener noreferrer' : ''}
                  variants={itemVariants} 
                  whileHover={{ y: -10 }} 
                  className="group relative rounded-2xl overflow-hidden bg-[#111] border border-white/5 hover:border-blue-500/30 transition-all duration-500 block"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-6">
                    <span className="text-blue-400 text-xs font-bold uppercase tracking-wider">{project.category}</span>
                    <h3 className="text-white text-xl font-semibold font-['Clash_Display'] mt-2">{project.title}</h3>
                    <p className="text-slate-400 text-sm mt-2">{project.description}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                      <span className="text-green-400 font-semibold text-sm">{project.result}</span>
                      <span className="text-blue-400 text-sm group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        {project.link.startsWith('http') ? 'Visit Site' : 'Get Started'} <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== CLIENT BENEFITS SECTION ===== */}
      <section className="py-24 sm:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants}>
            <div className="text-center mb-16">
              <motion.span variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-500/20 mb-6">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-400">Client Benefits</span>
              </motion.span>
              <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Clash_Display']">
                What You <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Get</span>
              </motion.h2>
            </div>

            <motion.div variants={containerVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {clientBenefits.map((benefit, index) => (
                <motion.div key={benefit.title} variants={itemVariants} whileHover={{ y: -5 }} className="p-6 rounded-2xl bg-gradient-to-b from-white/5 to-transparent border border-white/5 hover:border-blue-500/30 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-4">
                    <benefit.icon className="text-blue-400" size={24} />
                  </div>
                  <h3 className="text-white font-semibold text-lg font-['Clash_Display'] mb-2">{benefit.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{benefit.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== PROCESS SECTION - Interactive Timeline ===== */}
      <section className="py-24 sm:py-32 bg-[#0a0a0a] relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-600/10 blur-[150px] rounded-full animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-cyan-500/10 blur-[120px] rounded-full" />
        </div>
        
        {/* Grid Lines */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0, 87, 255, 0.4) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants}>
            <div className="text-center mb-20">
              <motion.span variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-500/20 mb-6">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-400">Our Process</span>
              </motion.span>
              <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Clash_Display']">
                From Vision to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Reality</span>
              </motion.h2>
            </div>

            {/* Interactive Process Cards */}
            <div className="relative">
              {/* Connection Line */}
              <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
              
              <motion.div variants={containerVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {processSteps.map((step, index) => (
                  <motion.div 
                    key={step.num} 
                    variants={itemVariants}
                    whileHover={{ scale: 1.05, y: -10 }}
                    className="group relative"
                  >
                    {/* Glowing Orb Connector */}
                    <div className="hidden lg:flex absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                      <motion.div 
                        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                        className="w-6 h-6 rounded-full bg-blue-500 shadow-lg shadow-blue-500/50"
                      />
                    </div>

                    <div className="relative p-8 rounded-3xl bg-gradient-to-b from-[#111]/80 to-[#0a0a0a]/80 backdrop-blur-xl border border-white/10 group-hover:border-blue-500/50 transition-all duration-500 overflow-hidden">
                      {/* Hover Glow */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/0 to-blue-600/0 group-hover:from-blue-600/10 group-hover:to-cyan-600/5 transition-all duration-500" />
                      
                      {/* Number Badge */}
                      <div className="relative z-10 mb-6">
                        <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 text-white text-2xl font-bold font-['Clash_Display'] shadow-lg shadow-blue-600/30 group-hover:shadow-blue-500/50 transition-all">
                          {step.num}
                        </span>
                      </div>
                      
                      <h3 className="relative z-10 text-white font-bold text-xl font-['Clash_Display'] mb-3">{step.title}</h3>
                      <p className="relative z-10 text-slate-400 text-sm leading-relaxed">{step.description}</p>
                      
                      {/* Arrow indicator */}
                      <motion.div 
                        initial={{ opacity: 0, x: -10 }}
                        whileHover={{ opacity: 1, x: 0 }}
                        className="absolute bottom-6 right-6 text-blue-400"
                      >
                        <ArrowRight size={20} />
                      </motion.div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== TESTIMONIALS SECTION - Bento Style ===== */}
      <section className="py-24 sm:py-32 bg-[#050505] relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/5 blur-[150px] rounded-full" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants}>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
              <div>
                <motion.span variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-500/20 mb-6">
                  <Star className="w-4 h-4 text-blue-400" fill="currentColor" />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-400">Client Love</span>
                </motion.span>
                <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Clash_Display']">
                  Trusted by <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Industry Leaders</span>
                </motion.h2>
              </div>
              <motion.div variants={itemVariants} className="flex items-center gap-4">
                <button onClick={prevTestimonial} data-testid="testimonial-prev-btn" className="w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-blue-600/20 hover:border-blue-500/50 transition-all duration-300">
                  <ChevronLeft size={24} />
                </button>
                <button onClick={nextTestimonial} data-testid="testimonial-next-btn" className="w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-blue-600/20 hover:border-blue-500/50 transition-all duration-300">
                  <ChevronRight size={24} />
                </button>
              </motion.div>
            </div>

            {testimonials.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Main Featured Testimonial */}
                <motion.div 
                  variants={itemVariants}
                  className="lg:col-span-7 relative"
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentTestimonial}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4 }}
                      data-testid="testimonial-slider"
                      className="relative h-full rounded-3xl bg-gradient-to-br from-blue-600/20 via-[#111]/80 to-[#0a0a0a] backdrop-blur-xl border border-blue-500/20 p-8 sm:p-10 overflow-hidden"
                    >
                      {/* Decorative Elements */}
                      <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 blur-[80px] rounded-full" />
                      <div className="absolute -top-4 -left-4 text-[120px] text-blue-500/10 font-serif leading-none">"</div>
                      
                      <div className="relative z-10">
                        <div className="flex gap-1 mb-6">
                          {[...Array(testimonials[currentTestimonial]?.rating || 5)].map((_, i) => (
                            <Star key={i} size={24} className="fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                        
                        <p className="text-xl sm:text-2xl lg:text-3xl text-white leading-relaxed mb-8 font-['Clash_Display'] font-medium">
                          "{testimonials[currentTestimonial]?.content}"
                        </p>
                        
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-xl">
                            {testimonials[currentTestimonial]?.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-white font-semibold text-lg">{testimonials[currentTestimonial]?.name}</p>
                            <p className="text-blue-400">{testimonials[currentTestimonial]?.role} at {testimonials[currentTestimonial]?.company}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </motion.div>

                {/* Side Stats/Info Cards */}
                <motion.div variants={itemVariants} className="lg:col-span-5 grid grid-cols-2 gap-4">
                  {/* Rating Card */}
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="col-span-2 p-6 rounded-2xl bg-gradient-to-br from-[#111] to-[#0a0a0a] border border-white/10 hover:border-blue-500/30 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-4xl font-bold text-white font-['Clash_Display']">5.0</p>
                        <p className="text-slate-400 text-sm">Average Rating</p>
                      </div>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={20} className="fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                  </motion.div>

                  {/* Happy Clients */}
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="p-6 rounded-2xl bg-gradient-to-br from-blue-600/20 to-[#0a0a0a] border border-blue-500/20 hover:border-blue-500/40 transition-all"
                  >
                    <Users className="text-blue-400 mb-3" size={28} />
                    <p className="text-3xl font-bold text-white font-['Clash_Display']">50+</p>
                    <p className="text-slate-400 text-sm">Happy Clients</p>
                  </motion.div>

                  {/* Satisfaction Rate */}
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="p-6 rounded-2xl bg-gradient-to-br from-cyan-600/20 to-[#0a0a0a] border border-cyan-500/20 hover:border-cyan-500/40 transition-all"
                  >
                    <TrendingUp className="text-cyan-400 mb-3" size={28} />
                    <p className="text-3xl font-bold text-white font-['Clash_Display']">95%</p>
                    <p className="text-slate-400 text-sm">Satisfaction</p>
                  </motion.div>

                  {/* Projects */}
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="col-span-2 p-6 rounded-2xl bg-[#111] border border-white/10 hover:border-white/20 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Award className="text-blue-400" size={24} />
                        <span className="text-white font-medium">Proven Track Record</span>
                      </div>
                      <span className="text-blue-400 font-bold">150+ Projects</span>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            )}

            {/* Testimonial Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button 
                  key={index} 
                  onClick={() => setCurrentTestimonial(index)} 
                  className={`h-2 rounded-full transition-all duration-300 ${index === currentTestimonial ? 'w-10 bg-gradient-to-r from-blue-500 to-cyan-500' : 'w-2 bg-white/20 hover:bg-white/40'}`} 
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== CTA SECTION - Immersive Design ===== */}
      <section className="py-32 sm:py-40 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-[#050505]">
          {/* Gradient Orbs */}
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-blue-600/20 blur-[200px] rounded-full"
          />
          <motion.div 
            animate={{ 
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-cyan-500/15 blur-[150px] rounded-full"
          />
        </div>

        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 87, 255, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 87, 255, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }} />

        {/* Floating Elements */}
        <motion.div 
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-20 text-blue-500/20 text-6xl font-mono"
        >{'</>'}</motion.div>
        <motion.div 
          animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 right-20 text-cyan-500/20 text-5xl font-mono"
        >{'{ }'}</motion.div>
        <motion.div 
          animate={{ x: [0, 10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/3 right-1/4 text-blue-500/10 text-4xl font-mono"
        >{'=>'}</motion.div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants}>
            {/* Badge */}
            <motion.div variants={itemVariants} className="flex justify-center mb-8">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 backdrop-blur-sm">
                <Zap className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-semibold text-blue-300">Start Your Project Today</span>
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h2 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-white font-['Clash_Display'] mb-8 leading-tight">
              Let's Build Something
              <br />
              <span className="relative">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500">Extraordinary</span>
                <motion.span 
                  animate={{ width: ['0%', '100%', '0%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"
                />
              </span>
            </motion.h2>
            
            <motion.p variants={itemVariants} className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-12">
              Ready to transform your digital presence? Book a free 15-minute strategy call and let's discuss how we can help you dominate your market.
            </motion.p>
            
            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/quote" 
                data-testid="cta-get-quote-btn" 
                className="group relative inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold text-lg px-10 py-5 rounded-full transition-all duration-500 shadow-2xl shadow-blue-600/30 hover:shadow-blue-500/50 overflow-hidden"
              >
                <span className="relative z-10">Get Your Free Quote</span>
                <ArrowRight size={22} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </Link>
              
              <Link 
                to="/services" 
                className="group inline-flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 text-white font-semibold text-lg px-10 py-5 rounded-full border border-white/20 hover:border-blue-500/50 transition-all duration-300 backdrop-blur-sm"
              >
                View Our Work
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div variants={itemVariants} className="mt-16 pt-12 border-t border-white/10">
              <p className="text-slate-500 text-sm mb-6">Trusted by innovative companies</p>
              <div className="flex flex-wrap justify-center items-center gap-8 opacity-50">
                <span className="text-xl font-bold text-white/60 font-['Clash_Display']">Caveman Financial</span>
                <span className="text-xl font-bold text-white/60 font-['Clash_Display']">Plan T</span>
                <span className="text-xl font-bold text-white/60 font-['Clash_Display']">UniGem</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
